import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { registerFeedbackController } from "./feedbackApi";
import "./FeedbackProvider.scss";

let toastId = 0;

function ToastIcon({ variant }) {
  if (variant === "success") return <span className="feedback-toast__icon">✓</span>;
  if (variant === "error") return <span className="feedback-toast__icon">!</span>;
  if (variant === "warning") return <span className="feedback-toast__icon">!</span>;
  return <span className="feedback-toast__icon">i</span>;
}

/**
 * @param {{ theme?: 'patient' | 'admin' }} props
 */
export function FeedbackProvider({ children, theme = "patient" }) {
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [confirmState, setConfirmState] = useState(null);
  const confirmResolveRef = useRef(null);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const alertImpl = useCallback((message, options = {}) => {
    const id = ++toastId;
    const variant = options.variant || "info";
    const title = options.title || null;
    const duration = typeof options.duration === "number" ? options.duration : 4800;
    const actions = Array.isArray(options.actions)
      ? options.actions
          .map((a) => ({
            label: String(a?.label || "").trim(),
            onClick: typeof a?.onClick === "function" ? a.onClick : null,
            kind: a?.kind === "primary" ? "primary" : "secondary",
          }))
          .filter((a) => a.label && a.onClick)
          .slice(0, 2)
      : [];

    setToasts((prev) => [...prev, { id, message, variant, title, actions }]);

    if (duration > 0) {
      window.setTimeout(() => removeToast(id), duration);
    }
  }, [removeToast]);

  const confirmImpl = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      confirmResolveRef.current = resolve;
      setConfirmState({
        title: options.title || "Confirm",
        message,
        confirmText: options.confirmText || "OK",
        cancelText: options.cancelText || "Cancel",
        danger: Boolean(options.danger),
      });
    });
  }, []);

  const closeConfirm = useCallback((result) => {
    setConfirmState(null);
    const fn = confirmResolveRef.current;
    confirmResolveRef.current = null;
    if (typeof fn === "function") fn(result);
  }, []);

  const loadingImpl = useCallback((visible, message = "") => {
    setLoading(Boolean(visible));
    setLoadingMessage(typeof message === "string" ? message : "");
  }, []);

  useEffect(() => {
    registerFeedbackController({
      alert: alertImpl,
      confirm: confirmImpl,
      setLoading: loadingImpl,
    });
    return () => registerFeedbackController(null);
  }, [alertImpl, confirmImpl, loadingImpl]);

  const rootClass =
    theme === "admin" ? "feedback-root feedback-root--admin" : "feedback-root";

  const portal =
    typeof document !== "undefined"
      ? createPortal(
          <div className={rootClass} data-feedback-theme={theme}>
            {toasts.length > 0 && (
              <div className="feedback-toast-region" aria-live="polite">
                {toasts.map((t) => (
                  <div
                    key={t.id}
                    className={`feedback-toast feedback-toast--${t.variant}`}
                    role="status"
                  >
                    <ToastIcon variant={t.variant} />
                    <div className="feedback-toast__body">
                      {t.title && <p className="feedback-toast__title">{t.title}</p>}
                      <p className="feedback-toast__msg">{t.message}</p>
                      {Array.isArray(t.actions) && t.actions.length > 0 && (
                        <div className="feedback-toast__actions">
                          {t.actions.map((a, idx) => (
                            <button
                              key={`${t.id}-a-${idx}`}
                              type="button"
                              className={`feedback-toast__action feedback-toast__action--${a.kind}`}
                              onClick={() => {
                                try {
                                  a.onClick?.();
                                } finally {
                                  removeToast(t.id);
                                }
                              }}
                            >
                              {a.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="feedback-toast__close"
                      aria-label="Dismiss"
                      onClick={() => removeToast(t.id)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {confirmState && (
              <div
                className="feedback-dialog-backdrop"
                role="presentation"
                onClick={() => closeConfirm(false)}
              >
                <div
                  className="feedback-dialog"
                  role="alertdialog"
                  aria-modal="true"
                  aria-labelledby="feedback-dialog-title"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h2 id="feedback-dialog-title" className="feedback-dialog__title">
                    {confirmState.title}
                  </h2>
                  <p className="feedback-dialog__msg">{confirmState.message}</p>
                  <div className="feedback-dialog__actions">
                    <button
                      type="button"
                      className="feedback-dialog__btn feedback-dialog__btn--secondary"
                      onClick={() => closeConfirm(false)}
                    >
                      {confirmState.cancelText}
                    </button>
                    <button
                      type="button"
                      className={`feedback-dialog__btn ${
                        confirmState.danger
                          ? "feedback-dialog__btn--danger"
                          : "feedback-dialog__btn--primary"
                      }`}
                      onClick={() => closeConfirm(true)}
                    >
                      {confirmState.confirmText}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {loading && (
              <div className="feedback-loading" role="status" aria-live="polite">
                <div className="feedback-spinner" aria-hidden />
                {loadingMessage ? (
                  <span className="feedback-loading__label">{loadingMessage}</span>
                ) : null}
              </div>
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {children}
      {portal}
    </>
  );
}
